/**
 * edit-member-dialog.tsx — Platform sửa tài khoản nhân viên: họ tên, email đăng nhập, SĐT, vai trò, bộ phận.
 * Chỉ gửi trường đã đổi (PATCH /platform/orgs/:orgId/members/:userId). Đổi email → server đăng xuất mọi thiết bị.
 * Vai trò của chủ sở hữu không đổi ở đây (dùng "Chuyển quyền chủ sở hữu").
 */
import { useEffect, useState } from 'react'
import { Loader2, Save } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { platformApiError } from '@/lib/platform-client'
import { useMemberOptions, useUpdateOrgMember, type OrgMember } from '@/hooks/use-platform-org-manage'
import { memberChanges, memberFormErrors, memberFormOf, type MemberForm } from './profile-helpers'
import { reasonError } from './reason'

const NO_TEAM = '__none__'

export function EditMemberDialog({ orgId, member, onClose }: { orgId: string; member: OrgMember | null; onClose: () => void }) {
  const options = useMemberOptions(orgId, !!member)
  const update = useUpdateOrgMember(orgId)
  const [form, setForm] = useState<MemberForm | null>(null)
  const [reason, setReason] = useState('')
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    setForm(member ? memberFormOf(member) : null)
    setReason('')
    setSubmitted(false)
  }, [member])

  if (!member || !form) return null
  const isOwner = member.role === 'owner'
  const errors = memberFormErrors(form)
  const changes = memberChanges(form, member)
  const changed = Object.keys(changes).length > 0
  const reasonErr = reasonError(reason, false)
  const roles = (options.data?.roles ?? []).filter((r) => r.systemKey !== 'owner')
  const teams = options.data?.teams ?? []
  const set = (patch: Partial<MemberForm>) => setForm((f) => (f ? { ...f, ...patch } : f))

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitted(true)
    if (!member || Object.keys(errors).length || !changed || reasonErr) return
    try {
      const r = await update.mutateAsync({ userId: member.id, ...changes, reason: reason.trim() || undefined })
      toast.success(r.revokedSessions
        ? `Đã lưu · đổi email nên đã đăng xuất ${r.revokedSessions} phiên của ${member.fullName}`
        : `Đã lưu thông tin ${member.fullName}`)
      onClose()
    } catch (err) {
      toast.error(platformApiError(err))
    }
  }

  const fieldErr = (k: keyof MemberForm) => (submitted || form[k] !== memberFormOf(member)[k]) && errors[k]
    ? <p className="text-xs text-destructive">{errors[k]}</p> : null

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Sửa thông tin nhân viên</DialogTitle>
          <DialogDescription>Thay đổi lưu vào nhật ký Platform. Nhân viên thấy ngay ở lần tải trang kế tiếp.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="em-name">Họ tên</Label>
              <Input id="em-name" value={form.fullName} maxLength={100} onChange={(e) => set({ fullName: e.target.value })} />
              {fieldErr('fullName')}
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="em-email">Email đăng nhập</Label>
              <Input id="em-email" type="email" autoComplete="off" value={form.email} onChange={(e) => set({ email: e.target.value })} />
              {fieldErr('email')}
              {changes.email && (
                <p className="text-xs text-warning">
                  Đổi email sẽ đăng xuất nhân viên khỏi mọi thiết bị — đăng nhập lại bằng email mới, mật khẩu giữ nguyên.
                  {member.googleLinked && ' Tài khoản đang đăng nhập bằng Google — gỡ liên kết Google nếu email Google cũng đổi.'}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="em-phone">Số điện thoại</Label>
              <Input id="em-phone" inputMode="tel" placeholder="0912345678" value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
              {fieldErr('phone')}
            </div>
            <div className="space-y-1.5">
              <Label>Bộ phận / Nhóm</Label>
              <Select value={form.teamId || NO_TEAM} onValueChange={(v) => set({ teamId: v === NO_TEAM ? '' : v })} disabled={options.isLoading}>
                <SelectTrigger><SelectValue placeholder="Không thuộc bộ phận" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_TEAM}>Không thuộc bộ phận</SelectItem>
                  {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Vai trò</Label>
              {isOwner ? (
                <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                  Chủ sở hữu — muốn đổi, dùng "Chuyển quyền chủ sở hữu" cho người khác.
                </p>
              ) : (
                <Select value={form.roleId} onValueChange={(v) => set({ roleId: v })} disabled={options.isLoading}>
                  <SelectTrigger><SelectValue placeholder={member.roleName ?? 'Chọn vai trò'} /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="em-reason">Lý do (tuỳ chọn)</Label>
            <Textarea id="em-reason" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Ghi rõ lý do — lưu vào nhật ký quản trị Platform" />
            {reasonErr && reason.trim() && <p className="text-xs text-destructive">{reasonErr}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Huỷ</Button>
            <Button type="submit" disabled={!changed || update.isPending}>
              {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Lưu
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
